// New Hook using the built in MUI-X State
import {
  GridFilterModel,
  GridPaginationModel,
  GridSortModel,
} from "@mui/x-data-grid-pro";
import { useDispatch, useSelector } from "react-redux";
import { IInvoiceParams } from "shared";
import { useParams } from "react-router";

import {
  reset,
  resetFilter,
  resetPagination,
  resetSort,
  setFilterModel,
  setSortModel,
  setPaginationModel,
} from "../slices/invoiceTableModels";
import { RootState } from "../store";
import { useFetchInvoicesQuery } from "../services/api/invoiceApi/invoice";
import { buildInvoiceListParamsFromFilterModel } from "../lib/invoiceFilterParams";

export enum InvoiceTableFields {
  invoiceNumber = `invoiceNumber`,
  invoiceReference = `invoiceReference`,
  invoiceStatus = `invoiceStatus`,
  contactName = `contactName`,
  primaryContactName = `primaryContactName`,
  invoiceIssueDate = `invoiceIssueDate`,
  invoiceDueDate = `invoiceDueDate`,
  invoiceTotal = `invoiceTotal`,
  invoiceOutstanding = `invoiceOutstanding`,
  frozen = `frozen`,
  groupId = `groupId`,
  isLateFee = `IsLateFee`,
  daysOverdue = `daysOverdue`,
  invoiceSentStatus = `invoiceSentStatus`,
  currency = `currency`,
  hasPaymentPlan = `hasPaymentPlan`,
  hasPaymentPromise = `hasPaymentPromise`,
  hasOpenQuery = `hasOpenQuery`,
  expectedPaymentDate = `expectedPaymentDate`,
  amountLastChangedDate = `amountLastChangedDate`,
  invoiceTag = `invoiceTag`,
}

export enum InvoiceTableHiddenFields {}

export function useInvoices(options?: { skip?: boolean }) {
  const invoiceTableParams = useInvoiceTableModels();
  const { organisationId, contactId: contactIdParam } = useParams();

  const {
    filterModel,
    sortModel,
    paginationModel,
  } = invoiceTableParams;
  const searchParams: IInvoiceParams = buildInvoiceListParamsFromFilterModel(
    filterModel,
    sortModel,
    paginationModel,
  );

  if (contactIdParam) {
    // When contactID is present in URL, we must be on the contact page, so override it
    // And group
    searchParams.contactId = Number(contactIdParam);
    searchParams.groupId = undefined;
  }

  const result = useFetchInvoicesQuery({
    ...searchParams,
    organisationId,
  }, {
    skip: !organisationId || !!options?.skip,
    pollingInterval: 1000 * 15, // 15 seconds
    skipPollingIfUnfocused: true,
    refetchOnMountOrArgChange: true,
  });

  return result;
}

export function useInvoiceTableModels() {
  const dispatch = useDispatch();
  const invoiceTableModels = useSelector((s: RootState) => s.invoiceTabelModels);

  return {
    // State
    filterModel: invoiceTableModels.filterModel,
    sortModel: invoiceTableModels.sortModel,
    paginationModel: invoiceTableModels.paginationModel,

    // Actions
    setFilterModel: (model: GridFilterModel) => dispatch(setFilterModel(model)),
    setSortModel: (model: GridSortModel) => dispatch(setSortModel(model)),
    setPaginationModel: (model: GridPaginationModel) => dispatch(setPaginationModel(model)),
    resetModels: () => dispatch(reset()),
    resetSort: () => dispatch(resetSort()),
    resetFilter: () => dispatch(resetFilter()),
    resetPagination: () => dispatch(resetPagination()),
  };
}
